<template>
  <div class="jira-analytics-page px-4 py-4 min-h-screen bg-light-inset">
    <!-- ── 1. UNIFIED PAGE HEADER (Matches Spark's Standard Design) ── -->
    <div class="ms-page-header mb-4">
      <div class="ms-page-header__content d-flex justify-content-between align-items-center flex-wrap gap-3 w-100">
        <div class="d-flex align-items-center gap-3">
          <div class="ms-page-header__icon bg-white text-info shadow-sm rounded-circle d-flex align-items-center justify-content-center" style="width: 48px; height: 48px;">
            <i class="fa-brands fa-jira fs-5 text-primary"></i>
          </div>
          <div>
            <p class="ms-page-header__breadcrumb text-muted mb-1">
              Platforms Delivery <span class="ms-breadcrumb-sep">/</span> Jira Analytics
            </p>
            <h1 class="ms-page-header__title text-dark mb-0 fw-bold fs-4">Jira Agile Analytics</h1>
          </div>
        </div>

        <!-- ── 2. PAGE NAVIGATION TABS / CONNECTION CONTROLS ── -->
        <div class="control-actions d-flex gap-2">
          <button 
            @click="toggleMode" 
            class="btn btn-sm px-3 py-2 fw-bold transition-all shadow-xs"
            :class="credentials.mock ? 'btn-light border text-muted-custom' : 'btn-info text-white'"
          >
            <i class="fa me-1" :class="credentials.mock ? 'fa-wand-magic-sparkles' : 'fa-network-wired'"></i>
            {{ credentials.mock ? 'Demo Mode Active' : 'Live Atlassian Connection' }}
          </button>
          
          <button 
            v-if="!credentials.mock"
            @click="fetchAnalytics"
            class="btn btn-sm btn-white border px-3 py-2 fw-bold text-dark transition-all"
            :disabled="loading"
          >
            <i class="fa fa-rotate me-1" :class="{ 'fa-spin': loading }"></i> Refresh
          </button>
        </div>
      </div>
    </div>

    <!-- ── 3. CONNECTION / INTEGRATION SETTINGS PANEL (Light Spark Design) ── -->
    <transition name="fade-slide">
      <div v-if="showConfig" class="card border rounded-3 bg-white shadow-sm p-4 mb-4">
        <div class="card-header-premium mb-3 pb-3 border-bottom d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center gap-2">
            <i class="fa fa-sliders text-primary"></i>
            <h5 class="mb-0 fw-bold text-dark fs-6">Atlassian Cloud Integration Settings</h5>
          </div>
          <span class="badge bg-light text-muted border py-1 px-2 text-xs">Access Key Pre-configured</span>
        </div>
        
        <div class="row g-3">
          <div class="col-md-3">
            <label class="form-label text-muted-custom small fw-bold uppercase-label mb-1">Atlassian Domain</label>
            <input 
              v-model="credentials.domain" 
              type="text" 
              class="form-control bg-white text-dark border-secondary-light rounded focus-glow text-sm" 
              placeholder="e.g. vertex.atlassian.net"
              @input="onFieldChange"
            />
          </div>
          <div class="col-md-3">
            <label class="form-label text-muted-custom small fw-bold uppercase-label mb-1">Authorized Email</label>
            <input 
              v-model="credentials.email" 
              type="email" 
              class="form-control bg-white text-dark border-secondary-light rounded focus-glow text-sm" 
              placeholder="e.g. dev@company.com"
              @input="onFieldChange"
            />
          </div>
          <div class="col-md-3">
            <label class="form-label text-muted-custom small fw-bold uppercase-label mb-1">Atlassian API Token</label>
            <input 
              v-model="credentials.token" 
              type="password" 
              class="form-control bg-white text-dark border-secondary-light rounded focus-glow text-sm" 
              placeholder="••••••••••••••••••••"
              @input="onFieldChange"
            />
          </div>
          <div class="col-md-3">
            <label class="form-label text-muted-custom small fw-bold uppercase-label mb-1">JQL Query (Optional)</label>
            <input 
              v-model="credentials.jql" 
              type="text" 
              class="form-control bg-white text-dark border-secondary-light rounded focus-glow text-sm" 
              placeholder="project = SPARK AND status = Done"
              @input="onFieldChange"
            />
          </div>
        </div>

        <div class="d-flex justify-content-between align-items-center mt-4 pt-3 border-top flex-wrap gap-3">
          <div class="form-check d-flex align-items-center gap-2">
            <input 
              id="mockCheck" 
              v-model="credentials.mock" 
              type="checkbox" 
              class="form-check-input cursor-pointer"
              @change="fetchAnalytics"
            />
            <label for="mockCheck" class="form-check-label cursor-pointer text-muted-custom small">
              Run in Mock Demo Mode (Offline-friendly high fidelity simulation)
            </label>
          </div>
          <button 
            @click="fetchAnalytics" 
            class="btn btn-info text-white px-4 py-2 fw-bold text-sm shadow-xs d-flex align-items-center gap-2"
            :disabled="loading || (!credentials.mock && (!credentials.domain || !credentials.email || !credentials.token))"
          >
            <span v-if="loading" class="spinner-border spinner-border-sm" role="status"></span>
            <i v-else class="fa fa-plug-circle-check"></i>
            {{ credentials.mock ? 'Load Simulated Metrics' : 'Connect & Fetch Live Jira' }}
          </button>
        </div>

        <!-- Warn of fallbacks or active alerts -->
        <div v-if="liveError" class="alert alert-warning mt-3 border-0 d-flex align-items-center gap-2" style="border-radius: 8px;">
          <i class="fa fa-triangle-exclamation text-warning fs-5"></i>
          <div class="small">
            <strong class="text-dark">Live Connection Failed:</strong> {{ liveError }}. Falling back automatically to high fidelity offline metrics simulation.
          </div>
        </div>
      </div>
    </transition>

    <!-- ── 4. LOADING STATE ── -->
    <div v-if="loading && !analytics" class="d-flex flex-column align-items-center justify-content-center py-5">
      <div class="spinner-border text-info mb-3" style="width: 3rem; height: 3rem;" role="status"></div>
      <p class="text-muted fw-bold">Computing delivery throughput and cycle distributions...</p>
    </div>

    <!-- ── 5. MAIN METRIC DASHBOARD ── -->
    <div v-else-if="analytics" class="fade-in-content">
      <!-- Info Alert Banner (Spark Native Styling) -->
      <div class="card border-0 mb-4 shadow-sm" style="border-radius: 12px; overflow: hidden;">
        <div class="d-flex align-items-center justify-content-between p-3 flex-wrap gap-3" 
             :class="isMockData ? 'bg-light-purple-tint text-purple-custom border-start border-purple border-4' : 'bg-light-info-tint text-info-custom border-start border-info border-4'">
          <div class="d-flex align-items-center gap-3">
            <div class="banner-icon-circle rounded-circle d-flex align-items-center justify-content-center bg-white shadow-xs" style="width: 40px; height: 40px;">
              <i class="fa" :class="isMockData ? 'fa-wand-magic-sparkles text-purple' : 'fa-circle-check text-info'"></i>
            </div>
            <div>
              <h6 class="m-0 fw-bold text-dark text-sm">
                {{ isMockData ? 'Currently viewing High Fidelity Simulated Sandbox Metrics' : 'Successfully connected to Live Jira cloud REST APIs' }}
              </h6>
              <p class="m-0 text-muted text-xs mt-0.5">
                {{ isMockData ? 'Calculations, distributions, and lead times are generated in real-time on 48 synthetic issues with complete historical transition logs.' : 'Metrics successfully calculated from direct JQL queries in real-time.' }}
              </p>
            </div>
          </div>
          <div v-if="isMockData" class="badge bg-purple text-white px-2.5 py-1 text-xs">DEMO MODE</div>
          <div v-else class="badge bg-success text-white px-2.5 py-1 text-xs">LIVE API ACTIVE</div>
        </div>
      </div>

      <!-- High-Level Metric Cards (Unified left-border accents matching Spark Platform) -->
      <div class="row g-4 mb-4">
        <!-- Card 1: Total Issues -->
        <div class="col-xl-3 col-md-6">
          <div class="card border-start border-primary border-4 rounded-3 shadow-sm p-3 bg-white h-100 position-relative hover-lift">
            <div class="metric-icon text-muted opacity-25 position-absolute" style="right: 1.2rem; top: 1.2rem; font-size: 2rem;">
              <i class="fa fa-list-check"></i>
            </div>
            <div class="text-muted text-xs fw-bold uppercase-label mb-1">Scope Under Management</div>
            <div class="text-dark fw-bold fs-2 my-1">{{ analytics.summary.total_issues }}</div>
            <div class="text-muted text-xs">Issues Catalogued</div>
          </div>
        </div>

        <!-- Card 2: Completed SP -->
        <div class="col-xl-3 col-md-6">
          <div class="card border-start border-info border-4 rounded-3 shadow-sm p-3 bg-white h-100 position-relative hover-lift">
            <div class="metric-icon text-muted opacity-25 position-absolute" style="right: 1.2rem; top: 1.2rem; font-size: 2rem;">
              <i class="fa-solid fa-bolt"></i>
            </div>
            <div class="text-muted text-xs fw-bold uppercase-label mb-1">Aggregated Velocity</div>
            <div class="text-dark fw-bold fs-2 my-1">{{ analytics.summary.completed_story_points }} <span class="fs-6 fw-normal text-muted">SP</span></div>
            <div class="text-muted text-xs">Of {{ analytics.summary.total_story_points }} Total Story Points Completed</div>
          </div>
        </div>

        <!-- Card 3: Cycle Time -->
        <div class="col-xl-3 col-md-6">
          <div class="card border-start border-warning border-4 rounded-3 shadow-sm p-3 bg-white h-100 position-relative hover-lift">
            <div class="metric-icon text-muted opacity-25 position-absolute" style="right: 1.2rem; top: 1.2rem; font-size: 2rem;">
              <i class="fa-solid fa-hourglass-half"></i>
            </div>
            <div class="text-muted text-xs fw-bold uppercase-label mb-1">Mean Cycle Time</div>
            <div class="text-dark fw-bold fs-2 my-1">{{ analytics.cycle_time.avg_days }} <span class="fs-6 fw-normal text-muted">Days</span></div>
            <div class="text-muted text-xs">Time spent active "In Progress" to Done</div>
          </div>
        </div>

        <!-- Card 4: Lead Time -->
        <div class="col-xl-3 col-md-6">
          <div class="card border-start border-success border-4 rounded-3 shadow-sm p-3 bg-white h-100 position-relative hover-lift">
            <div class="metric-icon text-muted opacity-25 position-absolute" style="right: 1.2rem; top: 1.2rem; font-size: 2rem;">
              <i class="fa-solid fa-calendar-days"></i>
            </div>
            <div class="text-muted text-xs fw-bold uppercase-label mb-1">Mean Lead Time</div>
            <div class="text-dark fw-bold fs-2 my-1">{{ analytics.lead_time.avg_days }} <span class="fs-6 fw-normal text-muted">Days</span></div>
            <div class="text-muted text-xs">Total time spent creation to Done</div>
          </div>
        </div>
      </div>

      <!-- Advanced Analytics Charts Row -->
      <div class="row g-4 mb-4">
        <!-- Chart 1: Throughput and Velocity trend -->
        <div class="col-lg-8">
          <div class="card border rounded-3 bg-white shadow-sm p-4 h-100">
            <div class="card-header-premium mb-3 pb-3 border-bottom d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center gap-2">
                <i class="fa-solid fa-chart-simple text-info"></i>
                <h5 class="m-0 fw-bold text-dark fs-6">Weekly Velocity & Throughput Trend</h5>
              </div>
              <span class="badge bg-light text-muted border text-xs">Rolling 8 Weeks</span>
            </div>
            
            <div class="chart-wrapper">
              <!-- Inline custom SVG chart representing weeks -->
              <svg viewBox="0 0 600 240" class="svg-chart">
                <!-- Custom linear gradients for SVG bar charts inside page head block -->
                <defs>
                  <linearGradient id="blue-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stop-color="#38bdf8" />
                    <stop offset="100%" stop-color="#0284c7" />
                  </linearGradient>
                  <linearGradient id="violet-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stop-color="#c084fc" />
                    <stop offset="100%" stop-color="#7c3aed" />
                  </linearGradient>
                </defs>

                <!-- Grid Lines -->
                <line x1="40" y1="20" x2="580" y2="20" stroke="#f1f5f9" stroke-dasharray="3,3" />
                <line x1="40" y1="70" x2="580" y2="70" stroke="#f1f5f9" stroke-dasharray="3,3" />
                <line x1="40" y1="120" x2="580" y2="120" stroke="#f1f5f9" stroke-dasharray="3,3" />
                <line x1="40" y1="170" x2="580" y2="170" stroke="#f1f5f9" stroke-dasharray="3,3" />
                <line x1="40" y1="200" x2="580" y2="200" stroke="#cbd5e1" />

                <!-- Bars representation -->
                <g v-for="(week, index) in analytics.weekly_throughput" :key="week.week_start">
                  <!-- X-axis spacing -->
                  <g class="bar-group" @mouseover="hoveredWeek = week" @mouseleave="hoveredWeek = null">
                    <!-- Issue Count Bar (Neon Blue) -->
                    <rect 
                      :x="60 + index * 65" 
                      :y="200 - (week.issues_count * 15)" 
                      width="18" 
                      :height="week.issues_count * 15" 
                      rx="3"
                      class="chart-bar-blue"
                    />
                    <!-- Story Points Bar (Glowing Violet) -->
                    <rect 
                      :x="80 + index * 65" 
                      :y="200 - (week.story_points * 6)" 
                      width="18" 
                      :height="week.story_points * 6" 
                      rx="3"
                      class="chart-bar-violet"
                    />
                  </g>
                  <!-- Labels on X-axis -->
                  <text 
                    :x="78 + index * 65" 
                    y="218" 
                    text-anchor="middle" 
                    class="axis-text"
                  >
                    {{ formatDateShort(week.week_start) }}
                  </text>
                </g>

                <!-- Legend on Chart -->
                <g transform="translate(420, 10)">
                  <circle cx="10" cy="10" r="5" fill="#0284c7" />
                  <text x="20" y="14" class="legend-text">Issues Done</text>
                  <circle cx="110" cy="10" r="5" fill="#7c3aed" />
                  <text x="120" y="14" class="legend-text">Story Points</text>
                </g>
              </svg>

              <!-- Week Hover Tooltip Overlay -->
              <div class="tooltip-container" v-if="hoveredWeek">
                <div class="tooltip-premium card border shadow p-2">
                  <div class="tooltip-header fw-bold text-dark border-bottom pb-1 mb-1 small text-truncate">
                    Week of {{ hoveredWeek.week_start }}
                  </div>
                  <div class="tooltip-row d-flex align-items-center gap-2 text-xs">
                    <span class="bullet blue-bullet"></span>
                    <span class="text-muted">Issues Completed:</span>
                    <strong class="text-dark">{{ hoveredWeek.issues_count }}</strong>
                  </div>
                  <div class="tooltip-row d-flex align-items-center gap-2 text-xs mt-1">
                    <span class="bullet violet-bullet"></span>
                    <span class="text-muted">Story Points Resolved:</span>
                    <strong class="text-dark">{{ hoveredWeek.story_points }} SP</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Chart 2: Status breakdown and Lead/Cycle metrics -->
        <div class="col-lg-4">
          <div class="card border rounded-3 bg-white shadow-sm p-4 h-100 d-flex flex-column justify-content-between">
            <div>
              <div class="card-header-premium mb-3 pb-3 border-bottom d-flex align-items-center gap-2">
                <i class="fa-solid fa-pie-chart text-info"></i>
                <h5 class="m-0 fw-bold text-dark fs-6">Status Distribution</h5>
              </div>

              <!-- Gorgeous status bars with progress tracks -->
              <div class="status-bar-container">
                <div 
                  v-for="(count, status) in analytics.status_distribution" 
                  :key="status" 
                  class="status-row mb-3"
                >
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <span class="status-badge" :class="getStatusClass(status)">
                      {{ status }}
                    </span>
                    <span class="status-percent text-muted small">
                      <strong>{{ count }}</strong> ({{ getPercentage(count, analytics.summary.total_issues) }}%)
                    </span>
                  </div>
                  <div class="progress-premium-container bg-light" style="height: 6px; border-radius: 10px; overflow: hidden;">
                    <div 
                      class="progress-premium-bar" 
                      :class="getStatusProgressClass(status)"
                      :style="{ width: getPercentage(count, analytics.summary.total_issues) + '%' }"
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Distribution stats for Lead and Cycle Time -->
            <div class="mt-4 pt-3 border-top">
              <div class="card-header-premium mb-3 d-flex align-items-center gap-2">
                <i class="fa-solid fa-stopwatch text-info"></i>
                <h6 class="m-0 fw-bold text-dark text-xs uppercase-label">Flow Statistics (Days)</h6>
              </div>
              <div class="stats-comparison-panel">
                <div class="stats-header-grid">
                  <div>Metric</div>
                  <div>Avg</div>
                  <div>Median</div>
                  <div>Min</div>
                  <div>Max</div>
                </div>
                <div class="stats-comparison-row">
                  <div class="text-dark fw-bold text-truncate">Lead Time</div>
                  <div class="text-primary fw-bold">{{ analytics.lead_time.avg_days }}</div>
                  <div class="text-muted-custom">{{ analytics.lead_time.median_days }}</div>
                  <div class="text-muted">{{ analytics.lead_time.min_days }}</div>
                  <div class="text-muted">{{ analytics.lead_time.max_days }}</div>
                </div>
                <div class="stats-comparison-row">
                  <div class="text-dark fw-bold text-truncate">Cycle Time</div>
                  <div class="text-success fw-bold">{{ analytics.cycle_time.avg_days }}</div>
                  <div class="text-muted-custom">{{ analytics.cycle_time.median_days }}</div>
                  <div class="text-muted">{{ analytics.cycle_time.min_days }}</div>
                  <div class="text-muted">{{ analytics.cycle_time.max_days }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Issue Type, Priority distributions and CDF -->
      <div class="row g-4 mb-4">
        <!-- Priority Distribution -->
        <div class="col-md-6 col-lg-4">
          <div class="card border rounded-3 bg-white shadow-sm p-4 h-100">
            <div class="card-header-premium mb-3 pb-3 border-bottom d-flex align-items-center gap-2">
              <i class="fa-solid fa-circle-exclamation text-info"></i>
              <h5 class="m-0 fw-bold text-dark fs-6">Priority Distribution</h5>
            </div>
            <div class="priority-list">
              <div 
                v-for="(count, priority) in analytics.priority_distribution" 
                :key="priority" 
                class="priority-row d-flex justify-content-between align-items-center py-2 border-bottom"
              >
                <div class="d-flex align-items-center gap-2">
                  <span class="bullet" :class="getPriorityBulletClass(priority)"></span>
                  <span class="text-dark text-sm">{{ priority }}</span>
                </div>
                <div class="badge bg-light text-muted-custom border text-xs">{{ count }} issues</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Issue Type Distribution -->
        <div class="col-md-6 col-lg-4">
          <div class="card border rounded-3 bg-white shadow-sm p-4 h-100">
            <div class="card-header-premium mb-3 pb-3 border-bottom d-flex align-items-center gap-2">
              <i class="fa-solid fa-tags text-info"></i>
              <h5 class="m-0 fw-bold text-dark fs-6">Issue Types</h5>
            </div>
            <div class="type-list">
              <div 
                v-for="(count, typeName) in analytics.type_distribution" 
                :key="typeName" 
                class="type-row d-flex justify-content-between align-items-center py-2 border-bottom"
              >
                <div class="d-flex align-items-center gap-2">
                  <i class="fa text-xs" :class="getTypeIcon(typeName)"></i>
                  <span class="text-dark text-sm">{{ typeName }}</span>
                </div>
                <div class="badge bg-light-info-tint text-info-custom border-0 text-xs">{{ count }} issues</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Cumulative Flow Trend -->
        <div class="col-lg-4">
          <div class="card border rounded-3 bg-white shadow-sm p-4 h-100">
            <div class="card-header-premium mb-3 pb-3 border-bottom d-flex align-items-center gap-2">
              <i class="fa-solid fa-chart-line text-info"></i>
              <h5 class="m-0 fw-bold text-dark fs-6">Cumulative Flow Progress</h5>
            </div>
            <div class="trend-steps">
              <div 
                v-for="day in analytics.cumulative_flow" 
                :key="day.date" 
                class="trend-step-row d-flex justify-content-between align-items-center py-2 border-bottom"
              >
                <span class="axis-text text-muted fw-bold">{{ formatDateShort(day.date) }}</span>
                <div class="d-flex gap-2 text-xs">
                  <span class="text-primary-custom">Created: <strong>{{ day.created_cumulative }}</strong></span>
                  <span class="text-success-custom">Done: <strong>{{ day.resolved_cumulative }}</strong></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      credentials: {
        // Auto-populate the active read-only credentials that the user provided in previous conversations
        domain: 'vertex.atlassian.net',
        email: 'ricardo@vtxsecurity.com',
        token: 'ATATT3xFfGF0ZidH9j4YGhHUJH9DfG8fMng4upxKNpovIck4sxhzGsBPEjkDYOcCCEgGvzhDDFJYjUVbnYmxFnWpBYSrKL6p72z9nI0l92TAeRmuSgxLnjz-V7-JkpFIaIyHO1WxxKFi02gjwBBreOKbZ6uTWpm2wfSkoU60FcKERqkPmDfAdlo=EC6C688E',
        jql: 'project = ALKE AND status = Done',
        mock: false // Default to live connection using the provided read-only key!
      },
      showConfig: false,
      loading: false,
      analytics: null,
      isMockData: false,
      liveError: null,
      hoveredWeek: null
    };
  },
  mounted() {
    this.fetchAnalytics();
  },
  methods: {
    toggleMode() {
      this.showConfig = !this.showConfig;
    },
    onFieldChange() {
      // If domain/email/token are set, toggle mock off by default to encourage live connection
      if (this.credentials.domain && this.credentials.email && this.credentials.token) {
        this.credentials.mock = false;
      }
    },
    async fetchAnalytics() {
      this.loading = true;
      this.liveError = null;

      try {
        const payload = {
          domain: this.credentials.domain,
          email: this.credentials.email,
          token: this.credentials.token,
          jql: this.credentials.jql,
          mock: this.credentials.mock
        };

        const res = await this.$axios.post('/api/jira/analytics', payload);
        if (res.data && res.data.success) {
          this.analytics = res.data.data;
          this.isMockData = res.data.is_mock;
          if (res.data.live_error) {
            this.liveError = res.data.live_error;
          }
        } else {
          throw new Error(res.data.message || 'Server calculation failed');
        }
      } catch (err) {
        console.error('Failed to load Jira metrics:', err);
        this.liveError = err.message || 'Connection or parsing timeout';
        // Auto fallback to mock data on error so dashboard doesn't stay blank
        this.isMockData = true;
        this.analytics = null;
        this.loadMockFallback();
      } finally {
        this.loading = false;
      }
    },
    loadMockFallback() {
      // In case backend endpoint has an issue, provide local frontend mock data rendering
      this.credentials.mock = true;
      this.fetchAnalytics();
    },
    formatDateShort(dateStr) {
      if (!dateStr) return '';
      // Parse YYYY-MM-DD
      const pts = dateStr.split('-');
      if (pts.length < 3) return dateStr;
      
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const mIdx = parseInt(pts[1], 10) - 1;
      return `${pts[2]} ${months[mIdx]}`;
    },
    getPercentage(count, total) {
      if (!total) return 0;
      return Math.round((count / total) * 100);
    },
    getStatusClass(status) {
      const s = status.toLowerCase();
      if (s === 'done' || s === 'resolved' || s === 'closed') return 'badge bg-success-tint text-success-custom';
      if (s === 'in progress' || s === 'active' || s === 'in dev') return 'badge bg-primary-tint text-primary-custom';
      if (s === 'in review' || s === 'review' || s === 'qa') return 'badge bg-warning-tint text-warning-custom';
      return 'badge bg-light text-muted-custom';
    },
    getStatusProgressClass(status) {
      const s = status.toLowerCase();
      if (s === 'done' || s === 'resolved' || s === 'closed') return 'bg-success';
      if (s === 'in progress' || s === 'active' || s === 'in dev') return 'bg-primary';
      if (s === 'in review' || s === 'review' || s === 'qa') return 'bg-warning';
      return 'bg-secondary';
    },
    getPriorityBulletClass(priority) {
      const p = priority.toLowerCase();
      if (p === 'highest') return 'bg-danger';
      if (p === 'high') return 'bg-warning';
      if (p === 'medium') return 'bg-primary';
      return 'bg-secondary';
    },
    getTypeIcon(typeName) {
      const t = typeName.toLowerCase();
      if (t === 'bug') return 'fa-bug text-danger';
      if (t === 'story') return 'fa-bookmark text-success';
      return 'fa-check-square text-info';
    }
  }
};
</script>

<style scoped>
.jira-analytics-page {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  color: #334155;
  background-color: #f8fafc;
}

/* Accent labels */
.uppercase-label {
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 0.75rem;
}

.text-muted-custom {
  color: #64748b !important;
}

/* Hover effect on metric cards */
.hover-lift {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.hover-lift:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.03) !important;
}

/* SVG Chart styling */
.chart-wrapper {
  position: relative;
  width: 100%;
}

.svg-chart {
  width: 100%;
  height: auto;
  overflow: visible;
  background: transparent;
  padding: 0;
}

.chart-bar-blue {
  fill: url(#blue-gradient);
  transition: opacity 0.2s ease;
  cursor: pointer;
}

.chart-bar-blue:hover {
  opacity: 0.85;
}

.chart-bar-violet {
  fill: url(#violet-gradient);
  transition: opacity 0.2s ease;
  cursor: pointer;
}

.chart-bar-violet:hover {
  opacity: 0.85;
}

.axis-text {
  fill: #64748b;
  font-size: 9px;
  font-weight: 500;
}

.legend-text {
  fill: #475569;
  font-size: 10px;
  font-weight: 500;
}

/* Tooltips style */
.tooltip-container {
  position: absolute;
  top: 10px;
  left: 40px;
  pointer-events: none;
}

.tooltip-premium {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  min-width: 180px;
}

.bullet {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
}

.blue-bullet { background: #0284c7; }
.violet-bullet { background: #7c3aed; }

/* Status Badges in Light Mode */
.status-badge {
  font-size: 0.72rem;
  font-weight: 700;
  padding: 0.15rem 0.5rem;
  border-radius: 12px;
  text-transform: uppercase;
}

/* Color tints */
.bg-success-tint { background-color: #ecfdf5 !important; }
.text-success-custom { color: #047857 !important; }

.bg-primary-tint { background-color: #eff6ff !important; }
.text-primary-custom { color: #1d4ed8 !important; }

.bg-warning-tint { background-color: #fffbeb !important; }
.text-warning-custom { color: #b45309 !important; }

.bg-light-purple-tint { background-color: #faf5ff !important; }
.text-purple-custom { color: #6b21a8 !important; }

.bg-light-info-tint { background-color: #f0fdfa !important; }
.text-info-custom { color: #0f766e !important; }

/* Flow Statistics Grid */
.stats-comparison-panel {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.stats-header-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
  font-size: 0.72rem;
  text-transform: uppercase;
  color: #64748b;
  font-weight: 700;
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 0.3rem;
  letter-spacing: 0.05em;
}

.stats-comparison-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
  font-size: 0.82rem;
  padding: 0.4rem 0;
  align-items: center;
  border-bottom: 1px solid #f8fafc;
}

/* Cumulative Flow steps */
.trend-steps {
  max-height: 190px;
  overflow-y: auto;
  padding-right: 0.4rem;
}

.trend-steps::-webkit-scrollbar {
  width: 5px;
}
.trend-steps::-webkit-scrollbar-track {
  background: #f1f5f9;
}
.trend-steps::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}

/* Transitions */
.fade-slide-enter-active, .fade-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(-15px);
}
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-15px);
}
</style>
